import { useCallback, useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { Tooltip } from '@mui/material';
import Decimal from 'decimal.js';

import AcTooltip from '../AcTooltip/AcTooltip';
import Input from '../Input/Input';

import { CustomPricing } from './Pricing.types';

const PricingTableInputCell = ({
  dataTestId,
  pricePoint,
  onPriceUpdate,
  disabled = false,
  customPriceData
}: {
  dataTestId: string;
  pricePoint: number;
  onPriceUpdate: (price: Decimal, currencyCode: string) => void;
  customPriceData: CustomPricing;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState<string>(pricePoint.toString());
  
  const { maxPrice, minPrice } = useMemo(() => {
    const maxPrice = new Decimal(customPriceData.maxChargeAmount).div(
      customPriceData.multiplier
    );
    const minPrice = new Decimal(customPriceData.minChargeAmount).div(
      customPriceData.multiplier
    );
    return {
      maxPrice: Number(maxPrice.toFixed(2)),
      minPrice: Number(minPrice.toFixed(2))
    };
  }, [
    customPriceData.maxChargeAmount,
    customPriceData.minChargeAmount,
    customPriceData.multiplier
  ]);

  useEffect(() => {
    const displayValue =
      customPriceData.multiplier === 1
        ? pricePoint.toFixed(0)
        : pricePoint.toFixed(2);
    setInputValue(displayValue);
  }, [pricePoint, customPriceData.multiplier]);

  const handlePriceUpdate = useCallback(
    (value: string) => {
      const numericValue = parseFloat(value);
      if (
        value &&
        !isNaN(numericValue) &&
        numericValue >= minPrice &&
        numericValue <= maxPrice
      ) {
        onPriceUpdate(
          new Decimal(value).times(new Decimal(customPriceData.multiplier)),
          customPriceData.countryCode3
        );
      }
    },
    [
      minPrice,
      maxPrice,
      customPriceData.multiplier,
      customPriceData.countryCode3,
      onPriceUpdate
    ]
  );

  const errorMessage = useMemo(() => {
    const numericValue = parseFloat(inputValue);
    if (numericValue < minPrice) {
      return `Price must be larger than ${minPrice}`;
    }
    if (numericValue > maxPrice) {
      return `Price must be smaller than ${maxPrice}`;
    }
    return '';
  }, [inputValue, minPrice, maxPrice]);

  const isInvalid = errorMessage !== '';

  return (
    <div className="p-1 w-full">
      <Input
        type={isInvalid ? 'text' : 'number'}
        value={inputValue}
        min={minPrice}
        max={maxPrice}
        disabled={disabled}
        dataTestId={dataTestId}
        leftIcon={<span>{customPriceData.currencySymbol}</span>}
        leftIconStyles="text-base-mutedForeground h-full flex items-center"
        state={isInvalid ? 'error' : 'default'}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (
            customPriceData.multiplier === 1 &&
            (e.key === '.' || e.key === ',')
          ) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter') {
            handlePriceUpdate((e.target as HTMLInputElement).value);
          }
          e.stopPropagation();
        }}
        onBlur={(e) => {
          handlePriceUpdate(e.target.value);
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        rightIconStyles="text-base-destructive w-4 h-4"
        rightIcon={
          isInvalid ? (
            <Tooltip
              arrow
              title={<AcTooltip content={errorMessage} />}
              placement="top"
            >
              <span>
                <FontAwesomeIcon
                  cursor="pointer"
                  icon="fa-regular fa-circle-exclamation"
                />
              </span>
            </Tooltip>
          ) : undefined
        }
      />
    </div>
  );
};

export default PricingTableInputCell;
